import {
    Box,
    DropdownContent,
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownTrigger,
    IconButton,
    Kebab,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { ChangeEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import LaunchWorkflowDialog from "./launch-workflow-dialog/LaunchWorkflowDialog";
import ScheduleWorkflowDialog from "./schedule-workflow-dialog/ScheduleWorkflowDialog";
import SubWorkflowList from "./SubWorkflowList";
import style from "./workflow-kebab-menu.scss";
import DeleteConfirmationDialog from "components/confirmation/DeleteConfirmationDialog";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import FailedRedNotificationIcon from "components/icons/FailedRedNotificationIcon";
import { SuccessIcon } from "components/icons/SuccessIcon";
import { TitleIcon } from "components/icons/TitleIcon";
import layout from "components/layout/layout.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import Modal from "components/modal/Modal";
import ManageWorkflowWrapper from "components/workflows/manage-workflow-dialog/ManageWorkflowWrapper";
import { InitialTab } from "components/workflows/workflow-editor-v3/WorkflowEditorV3View";
import {
    AUTH_WORKFLOW_DEFAULT,
    AUTH_WORKFLOW_DELETE,
    AUTH_WORKFLOW_ENABLE,
    AUTH_WORKFLOW_LAUNCH,
    AUTH_WORKFLOW_SCHEDULE,
    AUTH_WORKFLOW_SECURED,
} from "domain/authority";
import { LaunchType, WorkflowEntityType, WorkflowTableData } from "domain/workflows";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie, isCurrentUserInternal } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { setWorkflowAutoRefreshState } from "services/workflows/workflowAutoRefreshRepository";
import { Profile, profileToNameMap, workflowService, WorkflowService } from "services/workflows/WorkflowService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { findScrollableAncestor, useCloseDropdownOnScroll } from "utils/useCloseDropdownOnScroll";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface Result {
    title: string;
    message: string;
    visible: boolean;
}

interface SetDefaultResult extends Result {
    loading: boolean;
    previousDefault?: string;
}

interface Props {
    onUpdate: () => void;
    workflowService: WorkflowService;
    updateVisible: boolean;
    onOpenWithV3?: (initialTabs: InitialTab[]) => void;
    setIsSidePanelOpen?: (isOpen: boolean) => void;
    setSelectedRow?: React.Dispatch<React.SetStateAction<WorkflowTableData | undefined>>;
    workflow: WorkflowTableData;
}

export const WorkflowsTableKebabMenu = (props: Props) => {
    const { t } = useTranslation();
    const userDetail = useSelector((state: StoreState) => state.userReducer.user);
    const [result, setResult] = React.useState<Result>({ title: "", message: "", visible: false });
    const hideResult = () => {
        setResult({ title: result.title, message: result.message, visible: false });
        props.onUpdate();
    };
    const theme = useTheme();
    const [setDefaultResult, setSetDefaultResult] = React.useState<SetDefaultResult>({
        title: "",
        message: "",
        visible: false,
        loading: true,
        previousDefault: undefined,
    });
    const hideSetDefaultResult = () => {
        setSetDefaultResult({
            title: setDefaultResult.title,
            message: setDefaultResult.message,
            visible: false,
            loading: false,
            previousDefault: setDefaultResult.previousDefault,
        });
        props.onUpdate();
    };
    const [deleteWorkflowModalVisible, setDeleteWorkflowModalVisible] = React.useState(false);
    const [okClicked, setOkClicked] = React.useState(false);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [editWorkflowVisible, setEditWorkflowVisible] = React.useState(false);
    const [editWorkflowEditorVisible, setEditWorkflowEditorVisible] = React.useState(false);
    const [deleteSubworkflowDialog, setDeleteSubworkflowDialog] = React.useState(false);
    const [updateWorkflowVisible, setUpdateWorkflowVisible] = React.useState(false);
    const [launchWorkflowVisible, setLaunchWorkflowVisible] = React.useState(false);
    const [scheduleWorkflowVisible, setScheduleWorkflowVisible] = React.useState(false);
    const [isDropdownOpen, setDropdownOpen] = React.useState(false);
    const containerRef = React.useRef<HTMLDivElement>(null);
    useCloseDropdownOnScroll(isDropdownOpen, findScrollableAncestor(containerRef.current));

    const deleteWorkflow = () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        props.workflowService
            .deleteWorkflow(props.workflow.uuid, abortController)
            .then(() => {
                setDeleteWorkflowModalVisible(false);
                setDeleteSubworkflowDialog(false);
                setResult({
                    title: t("DeleteWorkflow.workflowDeleted.workflowDeletedTitle"),
                    message: t("DeleteWorkflow.workflowDeleted.successMessage", { name: props.workflow.name }),
                    visible: true,
                });
                setOkClicked(false);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setOkClicked(false);
                    setDeleteWorkflowModalVisible(false);
                    setDeleteSubworkflowDialog(false);
                    setResult({
                        title: t("DeleteWorkflow.workflowDeleted.workflowNotDeletedTitle"),
                        message: t("DeleteWorkflow.workflowDeleted.failureMessage"),
                        visible: true,
                    });
                }
            });
    };

    const exportWorkflow = (encryption: boolean) => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        props.workflowService.export(props.workflow.uuid, encryption, abortController);
    };

    React.useEffect(() => {
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, []);

    const setDefault = (workflowUuid: string, showConfirmation: boolean) => {
        usageStatisticsService.sendEvent({
            category: Category.WORKFLOW,
            action: Action.SET_DEFAULT_WORKFLOW,
        });

        const abortController = new AbortController();
        abortControllers.push(abortController);
        setSetDefaultResult({
            title: t("Common.pleaseWait"),
            message: setDefaultResult.message,
            visible: true,
            loading: true,
            previousDefault: undefined,
        });
        props.workflowService
            .setDefault(workflowUuid, abortController)
            .then((response) => {
                if (showConfirmation) {
                    let message = t("setWorkflowStatus.default.successMessage", {
                        newDefault: response.newDefaultName,
                        product: profileToNameMap.get(response.profile.toLowerCase() as Profile),
                    });
                    if (response.oldDefaultUuid != null) {
                        message +=
                            " " +
                            t("setWorkflowStatus.default.successMessageWithOldDefault", {
                                oldDefault: response.oldDefaultName,
                            });
                    }
                    setSetDefaultResult({
                        title: t("setWorkflowStatus.default.successTitle"),
                        message: message,
                        visible: true,
                        loading: false,
                        previousDefault: response.oldDefaultUuid,
                    });
                } else {
                    setSetDefaultResult({
                        title: t("Common.pleaseWait"),
                        message: setDefaultResult.message,
                        visible: false,
                        loading: true,
                        previousDefault: undefined,
                    });
                    props.onUpdate();
                }
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setResult({
                        title: t("setWorkflowStatus.default.failureTitle"),
                        message: t("setWorkflowStatus.failureMessage"),
                        visible: true,
                    });
                }
            });
    };

    const setActive = (uuid: string, name: string, newStatus: boolean) => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const activeMessage = t("WorkflowsTable.toast.successActiveMessage", { name: name });
        const inactiveMessage = t("WorkflowsTable.toast.successInActiveMessage", { name: name });
        props.workflowService
            .setActive(uuid, newStatus, abortController)
            .then(() => {
                setSetDefaultResult({
                    title: t("Common.pleaseWait"),
                    message: setDefaultResult.message,
                    visible: false,
                    loading: true,
                    previousDefault: undefined,
                });
                props.onUpdate();
                toast(
                    <SuccessIcon
                        successClass={layout.customToastSuccessIcon}
                        color={theme.contentBackgroundColor}
                        text={newStatus ? activeMessage : inactiveMessage}
                    />,
                    {
                        closeButton: (closeToastProps) => (
                            <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                        ),
                        className: layout.customToastSuccessMessage,
                    }
                );
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setResult({
                        title: t("setWorkflowStatus.active.failureTitle"),
                        message: t("setWorkflowStatus.failureMessage"),
                        visible: true,
                    });
                }
            });
    };

    const toggleSecure = (uuid: string, name: string, secured: boolean) => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const securedMessage = t("WorkflowsTable.setWorkflowSecured.securedMessage", { name: name });
        const unsecuredMessage = t("WorkflowsTable.setWorkflowSecured.unsecuredMessage", { name: name });
        props.workflowService
            .setSecure(uuid, secured, abortController)
            .then(() => {
                toast(
                    <SuccessIcon
                        successClass={layout.customToastSuccessIcon}
                        color={theme.contentBackgroundColor}
                        text={secured ? securedMessage : unsecuredMessage}
                    />,
                    {
                        closeButton: (closeToastProps) => (
                            <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                        ),
                        className: layout.customToastSuccessMessage,
                    }
                );
                props.onUpdate();
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setResult({
                        title: t("WorkflowsTable.setWorkflowSecured.failureTitle"),
                        message: t("Common.resultErrorMessage"),
                        visible: true,
                    });
                }
            });
    };

    const undoSetDefault = () => {
        const previousDefaultUuid = setDefaultResult.previousDefault;
        if (previousDefaultUuid != undefined) {
            setDefault(previousDefaultUuid, false);
        }
    };

    const handleWorkflowDelete = () => {
        setOkClicked(true);
        deleteWorkflow();
    };

    const [disabled, setDisabled] = React.useState(true);
    const handleWorkflowUpdate = () => {
        setUpdateWorkflowVisible(true);
    };

    const handleConfirmation = (event: ChangeEvent<HTMLInputElement>) => {
        setDisabled(event.target.value !== t("Common.delete").toUpperCase());
    };

    const canModify =
        props.workflow.profile !== Profile.BMP ||
        !props.workflow.secured ||
        (isCurrentUserInternal() && userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_SECURED]));

    return (
        <>
            <Box ref={containerRef}>
                <DropdownMenu
                    dropdownOffset={"Xxs"}
                    closeOnSelection
                    position="bottom-start"
                    flipOptions={{
                        fallbackPlacements: ["top-start"],
                    }}
                    onDropdownOpenChange={(isOpen: boolean) => setDropdownOpen(isOpen)}
                    testId={testIds.common.primaryView.table.kebabMenu.itself}
                >
                    <DropdownTrigger>
                        <IconButton>
                            <Kebab />
                        </IconButton>
                    </DropdownTrigger>
                    <DropdownContent>
                        {props.updateVisible && canModify && (
                            <DropdownMenuItem
                                onClick={() => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.WORKFLOW,
                                        action: Action.UPDATE_WORKFLOW_INITIATED,
                                    });
                                    handleWorkflowUpdate();
                                }}
                                testId={testIds.workArea.workflows.table.kebabMenu.updateButton}
                            >
                                {t("Common.update")}
                            </DropdownMenuItem>
                        )}
                        {canModify && (
                            <DropdownMenuItem
                                onClick={() => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.WORKFLOW,
                                        action: Action.EXPORT_WORKFLOW,
                                    });
                                    exportWorkflow(false);
                                }}
                                testId={testIds.workArea.workflows.table.kebabMenu.exportButton}
                            >
                                {t("Common.export")}
                            </DropdownMenuItem>
                        )}
                        {props.workflow.profile === Profile.LUN && (
                            <DropdownMenuItem
                                onClick={() => {
                                    usageStatisticsService.sendEvent({
                                        category: Category.WORKFLOW,
                                        action: Action.EXPORT_ENCRYPTED,
                                    });
                                    exportWorkflow(true);
                                }}
                                testId={testIds.workArea.workflows.table.kebabMenu.exportEncryptedButton}
                            >
                                {t("WorkflowsTable.kebabMenu.exportEncrypted")}
                            </DropdownMenuItem>
                        )}
                        {props.workflow.profile === Profile.BMP && !hasTenantCookie()
                            ? userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_ENABLE]) &&
                              props.workflow.launchType !== LaunchType.MANUAL && (
                                  <>
                                      {props.workflow.enabled ? (
                                          <DropdownMenuItem
                                              onClick={() => {
                                                  setActive(props.workflow.uuid, props.workflow.name, false);
                                              }}
                                              testId={testIds.workArea.workflows.table.kebabMenu.setInActiveButton}
                                          >
                                              {t("WorkflowsTable.kebabMenu.setInActive")}
                                          </DropdownMenuItem>
                                      ) : (
                                          <DropdownMenuItem
                                              onClick={() => {
                                                  setActive(props.workflow.uuid, props.workflow.name, true);
                                              }}
                                              testId={testIds.workArea.workflows.table.kebabMenu.setActiveButton}
                                          >
                                              {t("WorkflowsTable.kebabMenu.setActive")}
                                          </DropdownMenuItem>
                                      )}
                                  </>
                              )
                            : !hasTenantCookie() &&
                              !props.workflow.defaultWorkflow.own &&
                              userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_DEFAULT]) && (
                                  <DropdownMenuItem
                                      onClick={() => {
                                          setDefault(props.workflow.uuid, true);
                                      }}
                                      testId={testIds.workArea.workflows.table.kebabMenu.setDefaultButton}
                                  >
                                      {t("WorkflowsTable.kebabMenu.setDefault")}
                                  </DropdownMenuItem>
                              )}
                        {userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_LAUNCH]) &&
                            props.workflow.launchType == LaunchType.MANUAL &&
                            props.workflow.profile === Profile.BMP && (
                                <DropdownMenuItem
                                    onClick={() => {
                                        setLaunchWorkflowVisible(true);
                                        usageStatisticsService.sendEvent({
                                            category: Category.WORKFLOW,
                                            action: Action.LAUNCH_WORKFLOW_INITIATED,
                                        });
                                    }}
                                    testId={testIds.workArea.workflows.table.kebabMenu.launchButton}
                                >
                                    {t("WorkflowsTable.kebabMenu.launch")}
                                </DropdownMenuItem>
                            )}
                        {userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_SCHEDULE]) &&
                            props.workflow.launchType == LaunchType.MANUAL &&
                            props.workflow.profile === Profile.BMP &&
                            canModify && (
                                <DropdownMenuItem
                                    onClick={() => {
                                        setScheduleWorkflowVisible(true);
                                        usageStatisticsService.sendEvent({
                                            category: Category.WORKFLOW,
                                            action: Action.CREATE_SCHEDULE,
                                        });
                                    }}
                                    testId={testIds.workArea.workflows.table.kebabMenu.scheduleButton}
                                >
                                    {t("WorkflowsTable.kebabMenu.schedule")}
                                </DropdownMenuItem>
                            )}
                        {userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_SECURED]) &&
                            props.workflow.profile === Profile.BMP && (
                                <DropdownMenuItem
                                    onClick={() => {
                                        toggleSecure(props.workflow.uuid, props.workflow.name, !props.workflow.secured);
                                    }}
                                    testId={testIds.workArea.workflows.table.kebabMenu.securedButton}
                                >
                                    {props.workflow.secured
                                        ? t("WorkflowsTable.kebabMenu.setUnsecured")
                                        : t("WorkflowsTable.kebabMenu.setSecured")}
                                </DropdownMenuItem>
                            )}

                        {userSessionService.userHasAllAuthorities([AUTH_WORKFLOW_DELETE]) && canModify && (
                            <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    errorStyle
                                    onClick={() => {
                                        usageStatisticsService.sendEvent({
                                            category: Category.WORKFLOW,
                                            action: Action.REMOVE_WORKFLOW,
                                        });
                                        props.workflow.connectedWorkflows.parentWorkflows.length
                                            ? setDeleteSubworkflowDialog(true)
                                            : setDeleteWorkflowModalVisible(true);
                                    }}
                                    testId={testIds.workArea.workflows.table.kebabMenu.deleteButton}
                                >
                                    {t("Common.delete")}
                                </DropdownMenuItem>
                            </>
                        )}
                    </DropdownContent>
                </DropdownMenu>
            </Box>

            <DeleteConfirmationDialog
                modalDisplayed={deleteWorkflowModalVisible}
                introductionMessage={t("DeleteWorkflow.introductionMessage", {
                    customerName: userDetail?.tenantName,
                    name: props.workflow.name,
                })}
                confirmationMessage={t("DeleteWorkflow.confirmationMessage")}
                handleCommand={handleWorkflowDelete}
                setModalDisplayed={() => setDeleteWorkflowModalVisible(false)}
                title={t("DeleteWorkflow.title")}
                loading={okClicked}
            />

            <Modal
                isOpen={result.visible}
                hideModal={hideResult}
                modalTitle={result.title}
                titleIcon={<TitleIcon iconType={"SUCCESS"} />}
            >
                <div className={style.resultContainer}>
                    <strong>{props.workflow.name}</strong> {result.message}
                </div>
                <div className={style.okButtonContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={hideResult}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>

            <ManageWorkflowWrapper
                modalTitle={"ManageWorkflowDialog.title.manage"}
                modalVisibility={editWorkflowEditorVisible}
                onUpdate={props.onUpdate}
                workflowService={props.workflowService}
                workflowUuid={props.workflow.uuid}
                onShowModal={setEditWorkflowEditorVisible}
                editorGeneration={props.workflow.editorGeneration}
                isEdit={true}
                product={props.workflow.profile}
                version={props.workflow.editorVersion}
                name={props.workflow.name}
                parentWorkflowsCount={props.workflow.connectedWorkflows.parentWorkflows.length}
                entityType={WorkflowEntityType.WORKFLOW}
            />

            <Modal
                isOpen={setDefaultResult.visible}
                hideModal={() => hideSetDefaultResult()}
                modalTitle={setDefaultResult.title}
                titleIcon={<TitleIcon iconType={"SUCCESS"} />}
            >
                {setDefaultResult.loading && <LoadingIndicator />}
                {!setDefaultResult.loading && (
                    <>
                        <div className={style.resultContainer}>{setDefaultResult.message}</div>
                        <div className={style.buttonContainer}>
                            {setDefaultResult.previousDefault != null && (
                                <button
                                    className={buttons.secondaryButtonWithoutIcon}
                                    onClick={undoSetDefault}
                                    data-testid={testIds.common.confirmationDialog.undoButton}
                                >
                                    {t("Common.undo")}
                                </button>
                            )}
                            <button
                                className={buttons.okOptionalButtonPrimary}
                                onClick={hideSetDefaultResult}
                                data-testid={testIds.common.confirmationDialog.confirmButton}
                            >
                                {t("Common.ok")}
                            </button>
                        </div>
                    </>
                )}
            </Modal>

            <SubWorkflowList
                visible={editWorkflowVisible}
                hide={setEditWorkflowVisible}
                openManageDialogVisible={setEditWorkflowEditorVisible}
                connectedWorkflows={props.workflow.connectedWorkflows}
            />

            <Modal
                isOpen={deleteSubworkflowDialog}
                hideModal={() => {
                    setDeleteSubworkflowDialog(false);
                }}
                modalTitle={t("DeleteWorkflow.title")}
            >
                {okClicked ? (
                    <LoadingIndicator />
                ) : (
                    <>
                        <div className={form.deleteContainer}>
                            <Trans
                                i18nKey={
                                    props.workflow.defaultWorkflow.own
                                        ? "DeleteWorkflow.defaultSubworkflowIntroductionMessagePre"
                                        : "DeleteWorkflow.subworkflowIntroductionMessagePre"
                                }
                                values={{
                                    name: props.workflow.name,
                                }}
                                components={[<strong key={"tenantType"} />]}
                            />
                        </div>
                        <div className={form.deleteContainer}>
                            {t("DeleteWorkflow.subworkflowAssociatedWith")}
                            <div className={style.listBox}>
                                <ul>
                                    {props.workflow.connectedWorkflows.parentWorkflows.map((each) => (
                                        <li key={each}>{each}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className={form.deleteContainer}>
                            {t("DeleteWorkflow.subworkflowIntroductionMessagePost", {
                                name: props.workflow.name,
                            })}
                        </div>

                        <div className={form.deleteContainer}>{t("DeleteWorkflow.confirmationMessage")}</div>

                        <div className={form.deleteContainer}>
                            <input
                                id="confirmInput"
                                type="text"
                                data-testid={testIds.common.confirmationDialog.confirmInput}
                                onChange={(event) => handleConfirmation(event)}
                                className={form.input}
                            />
                        </div>
                        <div className={classNames(buttons.okButtonContainer, form.buttonContainer)}>
                            <button
                                className={buttons.secondaryButtonWithoutIcon}
                                onClick={() => {
                                    setDisabled(true);
                                    setDeleteSubworkflowDialog(false);
                                }}
                                data-testid={testIds.common.dialog.closeButton}
                            >
                                {t("Common.cancel")}
                            </button>

                            <button
                                className={classNames(buttons.deleteButton, buttons.primaryButtonWithoutIcon)}
                                data-testid={testIds.common.confirmationDialog.confirmButton}
                                onClick={() => {
                                    setDisabled(true);
                                    handleWorkflowDelete();
                                }}
                                disabled={disabled}
                            >
                                {t("Common.delete")}
                            </button>
                        </div>
                    </>
                )}
            </Modal>

            <ManageWorkflowWrapper
                modalTitle={"ManageWorkflowDialog.updateWorkflowDialog.title"}
                modalVisibility={updateWorkflowVisible}
                onUpdate={() => {
                    props.onUpdate();
                }}
                workflowService={workflowService}
                onShowModal={() => {
                    setUpdateWorkflowVisible(false);
                }}
                product={props.workflow.profile}
                name={props.workflow.name}
                updateWorkflowUuid={props.workflow.uuid}
                version={props.workflow.editorVersion}
                entityType={WorkflowEntityType.WORKFLOW}
                onOpenWithV3={props.onOpenWithV3}
            />

            <LaunchWorkflowDialog
                visible={launchWorkflowVisible}
                initialParameters={props.workflow.parameters}
                onClose={() => setLaunchWorkflowVisible(false)}
                onLaunch={(parameters) => {
                    return workflowService
                        .launch(props.workflow.uuid, parameters)
                        .then(() => {
                            if (props.setSelectedRow) {
                                props.setSelectedRow(props.workflow);
                            }
                            props.setIsSidePanelOpen?.(true);
                            if (props.workflow.uuid) {
                                setWorkflowAutoRefreshState(props.workflow.uuid, true);
                            }
                            toast(
                                <SuccessIcon
                                    successClass={layout.customToastSuccessIcon}
                                    color={theme.contentBackgroundColor}
                                    text={t("LaunchWorkflow.succeeded", { workflowName: props.workflow.name })}
                                />,
                                {
                                    closeButton: (closeToastProps) => (
                                        <CustomToastCloseButton
                                            closeToast={{ ...closeToastProps }}
                                            color={theme.iconFillColor}
                                        />
                                    ),
                                    className: layout.customToastSuccessMessage,
                                }
                            );
                        })
                        .catch(() => {
                            toast(
                                <div className={style.toastErrorContainer}>
                                    <FailedRedNotificationIcon
                                        backgroundColor={theme.errorBackgroundColor}
                                        iconColor={theme.contentBackgroundColor}
                                        size={24}
                                    />
                                    <div className={style.toastErrorContent}>
                                        <div>{t("LaunchWorkflow.failed", { workflowName: props.workflow.name })}</div>
                                    </div>
                                </div>,
                                {
                                    closeButton: (closeToastProps) => (
                                        <CustomToastCloseButton
                                            closeToast={{ ...closeToastProps }}
                                            color={theme.iconFillColor}
                                        />
                                    ),
                                }
                            );
                        });
                }}
            />

            <ScheduleWorkflowDialog
                workflowUuid={props.workflow.uuid}
                workflowService={props.workflowService}
                visible={scheduleWorkflowVisible}
                onClose={() => setScheduleWorkflowVisible(false)}
                onUpdate={props.onUpdate}
                initialParameters={props.workflow.parameters}
            />
        </>
    );
};
